// ─── MONTHLY ANALYSIS ─────────────────────────────────────────────────────────
// Pure, client-side checks over one month's transactions. Every check here is
// modeled on a real incident recorded in TALLY_RULES — this is a read-only
// audit, never a fixer: there is no "apply" path anywhere in this file.
//
// Findings are grouped by GROUP_ORDER (import integrity first, categorization
// second, spending observations last) because a missing statement or a
// duplicate makes every number after it suspect — a spending swing is only
// interesting once the data underneath it is trusted.
import { isIncomeCat, isExpenseCat, isUncategorized, isBusinessAccount, catBucket, isKpiIncomeRow, fmt, fmtDate } from './constants';

export const GROUP_ORDER = { import: 0, categorization: 1, spending: 2 };
export const GROUP_LABELS = { import: 'Import integrity', categorization: 'Categorization', spending: 'Spending' };

const tupleKey = (date, amount, account) => `${String(date).slice(0,10)}|${Number(amount).toFixed(2)}|${account}`;

function daysInMonth(y, mIdx) { return new Date(y, mIdx + 1, 0).getDate(); }

// Postgres DATE columns arrive as full ISO timestamps at UTC midnight, not bare
// YYYY-MM-DD — parsing that directly against a local-midnight Date can drift a
// day near a boundary depending on timezone. Pin to local noon instead, same
// fix fmtDate already uses.
function dateOnly(ds) {
  return new Date(String(ds).slice(0, 10) + 'T12:00:00');
}

// "X" / "X and Y" / "X, Y and N others" — the suppression message has to name
// accounts, not just count them, so the operator knows what to go upload
// rather than being told a bare number that tells him nothing actionable.
function formatAccountList(names) {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  const rest = names.length - 2;
  return `${names[0]}, ${names[1]} and ${rest} other${rest === 1 ? '' : 's'}`;
}

// ── CHECK 1 — accounts gone quiet ───────────────────────────────────────────
// Modeled on: a missing statement is Cory's most common real problem, and
// nothing on the dashboard today would ever say so on its own. Returns one
// finding per account that's gone quiet, plus the full per-account activity
// list (fresh or not) so the card can render the always-visible "last
// imported" strip regardless of whether anything is actually wrong.
//
// activityAsOf is keyed by display_name and holds each account's last
// transaction date ON OR BEFORE this month's end — NOT the account's global
// last transaction date. A past month's accounts all accumulate further
// transactions after that month closes, so a global "last touched" can never
// read as silent relative to a month that's already over; this check would
// silently never fire on any past month if it used that number instead.
function checkSilentAccounts({ accounts, activityAsOf, mode, month, isCurrentMonth }) {
  const relevant = accounts
    .filter(a => a.account_type !== 'investment' && isBusinessAccount(a.display_name, accounts) === (mode === 'business'))
    .map(a => ({ display_name: a.display_name, last_transaction_date: activityAsOf.get(a.display_name) || null }))
    .filter(a => a.last_transaction_date);

  const monthEnd = new Date(month.y, month.m, daysInMonth(month.y, month.m));
  const today = new Date();
  // The trailing-14-day window can't be judged for a current month that's
  // less than 14 days old yet — there's nothing to be silent about.
  const windowEnd = isCurrentMonth ? today : monthEnd;
  const elapsedIntoWindow = (windowEnd - new Date(month.y, month.m, 1)) / 86400000;
  const activity = relevant.map(a => ({
    account: a.display_name,
    last_transaction_date: a.last_transaction_date,
  }));

  if (elapsedIntoWindow < 14) return { activity, findings: [] };

  const windowStart = new Date(windowEnd.getTime() - 13 * 86400000);
  const findings = relevant
    .filter(a => dateOnly(a.last_transaction_date) < windowStart)
    .map(a => ({
      key: `silent-${a.display_name}`, group: 'import',
      title: `${a.display_name}: nothing in the last 14 days`,
      body: `Last transaction was ${fmtDate(a.last_transaction_date)}. A statement covering the recent period is probably missing.`,
      action: { account: a.display_name },
    }));
  return { activity, findings };
}

// ── CHECK 2 — near-duplicates within the month ──────────────────────────────
// The 29-row incident, caught after the fact instead of at import time. Two
// live rows can never share (date, amount, account) AND an identical
// normalized description — makeId's id is the primary key, so a real
// collision there would already have been silently dropped by ON CONFLICT.
// Any tuple match found here therefore already has differing wording; no
// separate description check is needed.
function checkNearDuplicates(monthTxsAll) {
  const byTuple = new Map();
  monthTxsAll.forEach(t => {
    const key = tupleKey(t.date, t.amount, t.account);
    (byTuple.get(key) || byTuple.set(key, []).get(key)).push(t);
  });
  const findings = [];
  for (const [key, rows] of byTuple) {
    if (rows.length < 2) continue;
    findings.push({
      key: `dup-${key}`, group: 'import',
      title: `${rows.length} rows share date, amount and account`,
      body: `${fmtDate(rows[0].date)} · ${fmt(Math.abs(parseFloat(rows[0].amount)))} · ${rows[0].account} — ` +
            rows.map(r => `"${r.description}"`).join(' vs. '),
      action: { ids: rows.map(r => r.id) },
    });
  }
  return findings;
}

// ── CHECK 3 — negative amounts in income categories ─────────────────────────
// Income going negative has no legitimate "it's actually a refund" reading
// the way a positive expense-category amount does (TALLY_RULES: positive in
// an expense category is refunds, common and expected) — so unlike that
// case, this direction needs no fuzzy "obviously a refund" carve-out.
function checkNegativeIncome(monthTxs, mode, customCats) {
  const rows = monthTxs.filter(t => isIncomeCat(t.category, mode, customCats) && parseFloat(t.amount) < 0);
  if (!rows.length) return [];
  return [{
    key: 'negative-income', group: 'import',
    title: `${rows.length} negative amount${rows.length===1?'':'s'} in income categories`,
    body: rows.map(r => `${fmtDate(r.date)} ${fmt(parseFloat(r.amount))} "${r.description}"`).join('; '),
    action: { ids: rows.map(r => r.id) },
  }];
}

// ── CHECK 4 — trailing-hyphen fingerprint ───────────────────────────────────
// The Vancity Visa credit-sign bug's exact, documented fingerprint (TALLY_RULES
// "Parser traps"): a stray minus that the amount regex missed falls into the
// description instead, ending it in a trailing ASCII hyphen (codepoint 45).
function checkTrailingHyphen(monthTxs) {
  const rows = monthTxs.filter(t => /-\s*$/.test(t.description || ''));
  if (!rows.length) return [];
  return [{
    key: 'trailing-hyphen', group: 'import',
    title: `${rows.length} description${rows.length===1?'':'s'} end in a trailing hyphen`,
    body: `Matches the Vancity Visa credit-sign-bug fingerprint — check these weren't mis-signed. ` +
          rows.map(r => `"${r.description}"`).join('; '),
    action: { ids: rows.map(r => r.id) },
  }];
}

// ── CHECK 5 — pending at time of view ────────────────────────────────────────
function checkPending(monthTxs, isCurrentMonth) {
  const rows = monthTxs.filter(t => t.status === 'pending');
  if (!rows.length) return [];
  return [{
    key: 'pending', group: 'categorization',
    title: isCurrentMonth
      ? `${rows.length} pending so far this month`
      : `${rows.length} pending transaction${rows.length===1?'':'s'} never reviewed`,
    body: isCurrentMonth ? 'Still open — normal for a month in progress.' : 'This month is over; these were never confirmed.',
    action: { status: 'pending' },
  }];
}

// ── CHECK 6 — confirmed but still Uncategorized ─────────────────────────────
function checkConfirmedUncategorized(monthTxs) {
  const rows = monthTxs.filter(t => t.status === 'confirmed' && isUncategorized(t.category));
  if (!rows.length) return [];
  return [{
    key: 'confirmed-uncat', group: 'categorization',
    title: `${rows.length} confirmed row${rows.length===1?'':'s'} still Uncategorized`,
    body: 'Confirming an uncategorized row defeats the purpose — these were approved without ever being sorted.',
    action: { ids: rows.map(r => r.id) },
  }];
}

// ── CHECK 7 — a single row over $10,000 ─────────────────────────────────────
// Scoped to expense/income types only. transfer- and gift-typed categories
// (Questrade transfers, a house-fund payment) are SUPPOSED to carry large
// one-off amounts — re-flagging a correctly-categorized recurring transfer
// every month it happens is exactly the noise that trains an operator to
// ignore the card.
function checkLargeSingleRow(monthTxs, mode, customCats) {
  const rows = monthTxs.filter(t =>
    (isExpenseCat(t.category, mode, customCats) || isIncomeCat(t.category, mode, customCats)) &&
    Math.abs(parseFloat(t.amount)) > 10000);
  if (!rows.length) return [];
  return [{
    key: 'large-row', group: 'categorization',
    title: `${rows.length} transaction${rows.length===1?'':'s'} over $10,000`,
    body: rows.map(r => `${fmtDate(r.date)} ${fmt(parseFloat(r.amount))} "${r.description}" (${r.category})`).join('; ') +
          ' — both the $50,000 and $100,000 incidents hid this way.',
    action: { ids: rows.map(r => r.id) },
  }];
}

// ── CHECK 8 — category swing vs its own trailing 3-month average ───────────
function checkCategorySwings(monthTxs, priorMonthsTxs, mode, customCats) {
  const currentByCat = new Map();
  monthTxs.filter(t => isExpenseCat(t.category, mode, customCats)).forEach(t => {
    currentByCat.set(t.category, (currentByCat.get(t.category) || 0) + parseFloat(t.amount));
  });
  if (!priorMonthsTxs.length) return []; // no baseline to compare against yet

  const priorByCat = new Map();
  priorMonthsTxs.forEach(({ txs }) => {
    const seen = new Map();
    txs.filter(t => isExpenseCat(t.category, mode, customCats)).forEach(t => {
      seen.set(t.category, (seen.get(t.category) || 0) + parseFloat(t.amount));
    });
    seen.forEach((v, cat) => { (priorByCat.get(cat) || priorByCat.set(cat, []).get(cat)).push(v); });
  });

  const cats = new Set([...currentByCat.keys(), ...priorByCat.keys()]);
  const findings = [];
  cats.forEach(cat => {
    const current = Math.abs(currentByCat.get(cat) || 0);
    const priorVals = priorByCat.get(cat) || [];
    const avg = priorVals.length ? Math.abs(priorVals.reduce((s,v)=>s+v,0) / priorMonthsTxs.length) : 0;
    if (avg === 0) {
      // Brand new spending with no baseline — only worth a note above a
      // trivial amount, not every $8 one-off purchase in a fresh category.
      if (current >= 50) {
        findings.push({
          key: `swing-${cat}`, group: 'spending',
          title: `${cat}: new spending, no 3-month baseline`,
          body: `${fmt(current)} this month, nothing in the trailing 3 months.`,
          action: { cat },
        });
      }
      return;
    }
    const delta = (current - avg) / avg;
    if (Math.abs(delta) > 0.5) {
      findings.push({
        key: `swing-${cat}`, group: 'spending',
        title: `${cat}: ${delta > 0 ? 'up' : 'down'} ${Math.round(Math.abs(delta) * 100)}% vs its 3-month average`,
        body: `${fmt(current)} this month vs ${fmt(avg)} average.`,
        action: { cat },
      });
    }
  });
  return findings;
}

// ── CHECK 9 — buckets over target ────────────────────────────────────────────
// Reuses the exact formula the Needs/Wants/Savings card already renders — a
// bare "over target" fires almost every month for at least one bucket, since
// 50/30/20 is a soft aim, not a budget; a 5-point margin keeps this quiet
// unless the overage is actually worth a look.
function checkBuckets(monthTxs, mode, customCats, settings) {
  if (mode === 'business') return [];
  const bucketAcc = { needs: 0, wants: 0, savings: 0 };
  monthTxs.filter(t => isExpenseCat(t.category, mode, customCats) && !t.envelope_id).forEach(t => {
    const b = t.bucket_override || catBucket(t.category, mode, customCats);
    if (b) bucketAcc[b] += parseFloat(t.amount);
  });
  const netSpend = { needs: -bucketAcc.needs, wants: -bucketAcc.wants, savings: -bucketAcc.savings };
  const totalSpend = Object.values(netSpend).reduce((s, v) => s + Math.max(v, 0), 0);
  const findings = [];
  ['needs', 'wants', 'savings'].forEach(b => {
    const net = netSpend[b];
    if (net <= 0 || totalSpend <= 0) return;
    const pct = Math.round(net / totalSpend * 100);
    const target = settings[b + '_pct'] || 0;
    if (pct > target + 5) {
      findings.push({
        key: `bucket-${b}`, group: 'spending',
        title: `${b[0].toUpperCase()}${b.slice(1)} at ${pct}%, target ${target}%`,
        body: `${fmt(net)} this month — ${pct - target} points over target.`,
        action: { bucket: b },
      });
    }
  });
  return findings;
}

// ── CHECK 10 — envelopes drawn past budget ──────────────────────────────────
function checkEnvelopes(envelopes, mode) {
  if (mode === 'business') return [];
  return envelopes
    .filter(e => e.status === 'open' && e.view !== 'business' && parseFloat(e.spent||0) > parseFloat(e.budget||0))
    .map(e => ({
      key: `envelope-${e.id}`, group: 'spending',
      title: `${e.name}: ${fmt(parseFloat(e.spent) - parseFloat(e.budget))} over budget`,
      body: `${fmt(parseFloat(e.spent))} spent of ${fmt(parseFloat(e.budget))} budgeted.`,
      action: { envelope: e.id },
    }));
}

// ── CHECK 11 — income vs its trailing 3-month average ───────────────────────
function checkIncomeSwing(monthTxs, priorMonthsTxs, mode, customCats) {
  if (!priorMonthsTxs.length) return [];
  const current = monthTxs.filter(t => isKpiIncomeRow(t, mode, customCats)).reduce((s,t)=>s+parseFloat(t.amount), 0);
  const priorVals = priorMonthsTxs.map(({ txs }) =>
    txs.filter(t => isKpiIncomeRow(t, mode, customCats)).reduce((s,t)=>s+parseFloat(t.amount), 0));
  const avg = priorVals.reduce((s,v)=>s+v, 0) / priorMonthsTxs.length;
  if (avg === 0) return [];
  const delta = (current - avg) / avg;
  if (Math.abs(delta) <= 0.25) return [];
  return [{
    key: 'income-swing', group: 'spending',
    title: `Income ${delta > 0 ? 'up' : 'down'} ${Math.round(Math.abs(delta) * 100)}% vs its 3-month average`,
    body: `${fmt(current)} this month vs ${fmt(avg)} average.`,
    action: { kpi: 'income' },
  }];
}

// ── ENTRY POINT ──────────────────────────────────────────────────────────────
// monthTxsAll: current month, show_hidden=true, BOTH views (so checks 1/2 can
//   see transfer-hidden rows as evidence of activity / dup collisions).
// priorMonthsTxs: up to 3 prior months, [{y, m, txs}], view-filtered, default
//   (non-hidden) — only used for the trailing-average checks.
export function computeMonthlyFindings({
  monthTxsAll, priorMonthsTxs, accounts, activityAsOf, envelopes, settings, mode, customCats,
  month, isCurrentMonth,
}) {
  const monthTxs = monthTxsAll.filter(t => !t.transfer_hide && isBusinessAccount(t.account, accounts) === (mode === 'business'));
  const monthTxsAllView = monthTxsAll.filter(t => isBusinessAccount(t.account, accounts) === (mode === 'business'));
  // priorMonthsTxs arrives as raw fetches (both views mixed, like monthTxsAll
  // before the filter above) — view- and hidden-scope it the same way, or a
  // household trailing average would silently absorb business rows.
  const priorMonthsView = priorMonthsTxs.map(({ y, m, txs }) => ({
    y, m, txs: txs.filter(t => !t.transfer_hide && isBusinessAccount(t.account, accounts) === (mode === 'business')),
  }));

  const { activity, findings: silentFindings } = checkSilentAccounts({ accounts, activityAsOf, mode, month, isCurrentMonth });

  // Checks 8/9/11 compare the viewed month against a trailing average — that
  // comparison is only meaningful once the month is both OVER and FULLY
  // IMPORTED. Conflating those two facts is what caused the false swings this
  // gate exists to prevent: August 2026, viewed Sep 5, is calendar-complete
  // but Vancity Visa (Cory) — the largest source in 4 of 5 falsely-flagged
  // categories — hadn't had its August statement uploaded yet, so those
  // categories looked like they'd collapsed when the truth is the data for
  // them simply isn't in the table yet. Two independent gates, checked in
  // this order:
  //   1. isCurrentMonth: a month still in progress can never be compared to a
  //      full trailing month, regardless of how complete its statements are.
  //   2. silentFindings.length: reuses check 1's OWN live result — never the
  //      close-out button, which is a manual snapshot action and would let
  //      this go stale the moment a late statement lands after a month was
  //      closed out, or stay wrongly dark if nobody re-closes it.
  // checkEnvelopes is deliberately NOT gated here — an envelope's over-budget
  // status is a running all-time total, not a vs-trailing-average comparison,
  // so missing data can only make it read as less over budget than it truly
  // is (never a false alarm), unlike checks 8/9/11 which can swing either way.
  const spendingSuppressed = isCurrentMonth
    ? { reason: 'in-progress', message: 'Spending comparisons run once the month is complete.' }
    : silentFindings.length > 0
      ? { reason: 'stale-data', accounts: silentFindings.map(f => f.action.account),
          message: `Spending comparisons hidden — ${formatAccountList(silentFindings.map(f => f.action.account))} have incomplete data for this month.` }
      : null;

  const spendingComparisonFindings = spendingSuppressed ? [] : [
    ...checkCategorySwings(monthTxs, priorMonthsView, mode, customCats),
    ...checkBuckets(monthTxs, mode, customCats, settings),
    ...checkIncomeSwing(monthTxs, priorMonthsView, mode, customCats),
  ];

  const findings = [
    ...silentFindings,
    ...checkNearDuplicates(monthTxsAllView),
    ...checkNegativeIncome(monthTxs, mode, customCats),
    ...checkTrailingHyphen(monthTxs),
    ...checkPending(monthTxs, isCurrentMonth),
    ...checkConfirmedUncategorized(monthTxs),
    ...checkLargeSingleRow(monthTxs, mode, customCats),
    ...spendingComparisonFindings,
    ...checkEnvelopes(envelopes, mode),
  ].sort((a, b) => GROUP_ORDER[a.group] - GROUP_ORDER[b.group]);

  return { activity, findings, spendingSuppressed };
}
